import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, someTween } from '../skillUsage';

export const sharedAxisSlideSkill: CodeoSkill = {
    name: SKILL_NAMES.sharedAxisSlide,
    kind: 'codeo',
    description:
        'Move from one state to the next along a single shared axis, the outgoing state leaving ' +
        'the way the incoming one arrives — the plain, reliable phase change for a step, a page, ' +
        'or the next item in a sequence.',
    documentUsesSkill: check(
        (script) =>
            someTween(script, /\b[xy]:\s*-/, /opacity:\s*0\b/) &&
            someTween(script, /\b[xy]:\s*0\b/, /opacity:\s*1\b/),
    ),
    instructions: `# Shared Axis Slide

Two states travel along the same line: the first exits left as the second enters from the right, both fading through the crossover. Because they share an axis and a direction, the viewer reads a *sequence* — step two follows step one — rather than an unrelated replacement.

This is the workhorse phase change. It carries no drama, which is exactly why it works when the content is the point: steps in a process, items in a list being examined one at a time, before and after in a walkthrough.

## Markup

Both states occupy the same box so they can cross without either being displaced.

\`\`\`html
<div id="axis-stage" style="position: absolute; left: 260px; top: 320px; width: 1400px; height: 440px; overflow: hidden;">
  <div class="axis-state" id="step-one" style="position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: center; gap: 18px;">
    <div style="font-family: var(--l5-font-0), sans-serif; font-size: 34px; font-weight: 700; letter-spacing: 0.16em; color: var(--l5-color-2, #38bdf8);">STEP ONE</div>
    <div style="font-family: var(--l5-font-0), sans-serif; font-size: 104px; font-weight: 800; color: var(--l5-color-0, #101820);">Connect your data</div>
  </div>
  <div class="axis-state" id="step-two" style="position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: center; gap: 18px;">
    <div style="font-family: var(--l5-font-0), sans-serif; font-size: 34px; font-weight: 700; letter-spacing: 0.16em; color: var(--l5-color-2, #38bdf8);">STEP TWO</div>
    <div style="font-family: var(--l5-font-0), sans-serif; font-size: 104px; font-weight: 800; color: var(--l5-color-0, #101820);">Publish anywhere</div>
  </div>
</div>
\`\`\`

## Timeline code

The travel distance is short — about a fifth of the stage width. A full-width slide reads as a carousel, which is a UI pattern, not a motion-graphics one.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const TRAVEL = 260;
const SWAP = 2.6;

gsap.set('#step-one', { x: 0, opacity: 1 });
gsap.set('#step-two', { x: TRAVEL, opacity: 0 });

tl.to('#step-one', { x: -TRAVEL, opacity: 0, duration: 0.5, ease: 'power3.in' }, SWAP);
tl.to('#step-two', { x: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }, SWAP + 0.16);

window.__timelines = { main: tl };
\`\`\`

The 0.16s overlap is what makes it one move rather than two. The outgoing state uses \`power3.in\` (accelerating away) and the incoming \`power3.out\` (decelerating in), so the pair reads as a single continuous handoff.

## Adapt

- **Axis**: vertical (\`y\`) reads as descending through a list; horizontal reads as progressing through a sequence. Pick one per graphic and never mix.
- **Depth axis**: scaling the outgoing state to 0.92 as it fades and bringing the incoming from 1.08 reads as moving *through* rather than across — useful for entering a detail.
- **Direction carries meaning**: going back should reverse the axis. A "previous" that slides the same way as "next" is disorienting.
- **Three or more states**: chain the same pair of tweens per swap, keeping \`TRAVEL\` and the overlap identical each time. Consistency is what makes a sequence legible.
- **Persistent frame**: keep a heading or a progress indicator outside the sliding stage so something visible carries across the change.
- **Overlap**: 0.12–0.2s. At zero the states read as two separate events; past 0.3s they are simply cross-fading and the axis stops mattering.

## Pitfalls

- **The scroll and input plugins are not loaded.** \`drawSVG\`, \`morphSVG\`, \`splitText\` and the rest of the injected set are available, but ScrollTrigger, ScrollSmoother, ScrollToPlugin, Observer, Draggable and InertiaPlugin are not — a seeked, headless frame has no scroll position and no pointer for them to read. GSAP accepts an unrecognised property silently and animates nothing, so a progress rail driven by ScrollTrigger is a rail that never moves.
- **Never tween a colour *into* a \`var()\`.** GSAP interpolates between two resolved colours; given a \`var(--l5-color-N, …)\` end value it can only assign it, so a progress marker or a state chip snaps rather than blending. Resolve the token first: \`getComputedStyle(document.documentElement).getPropertyValue('--l5-color-2').trim()\`, keep the literal as a fallback, and tween to that.
- Both states are \`x\`-tweened, so neither may carry a CSS \`transform\`; position them with \`inset: 0\` inside the stage.
- The stage needs \`overflow: hidden\`, or the states are visible sliding in from outside it. If the design wants them visible beyond the stage, the elements need \`data-l5-allow-offscreen\` instead.
- Don't cross-fade without the slide. A pure dissolve between two full states is the slideshow failure the style guidance warns about; the shared axis is what makes it a rearrangement.
- Keep something on screen across the swap. If the entire frame changes, this is a cut, not a phase — see \`${SKILL_NAMES.elementMorphHandoff}\` for carrying an element through.
- Give each state real time. A swap every 1.5s is a slideshow at speed; each state needs long enough to be read.
- Don't let the last state land and then hold still for the rest of the scene — see \`${SKILL_NAMES.sustainedSceneReveal}\` for pacing the tail.
- \`gsap.set\` establishes both from-states at build time, so neither tween needs \`immediateRender: false\`.`,
};
